import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from "react-bootstrap/Button";
import { useState, useEffect, useRef } from 'react';
import { Card, Container, FloatingLabel, Form } from 'react-bootstrap';
import { useNavigate, useLocation } from "react-router-dom";
import useApi from '../hooks/use-api';
import LegalWarning from '../components/legalwarning';
import InsetAlert from '../components/insetalert';
import DotLoader from '../components/dotloader';
import styles from '../styles/auth-pages.module.css';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { applyAuthSession } from '../new/auth-session';
import { buildPasswordSchema, getPasswordRuleStates } from './password-policy';

const PASSWORD_SUCCESS_REDIRECT_DELAY_MS = 1600;

const validationSchema = buildPasswordSchema();

function SetPassword(){

    useEffect(() => {
        document.title = "MFP - Set Password";
    },[]);


    const navigate = useNavigate();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const PasswordToken = query.get("setpasstoken");

    const {error, postRequest} = useApi();
    const [showRecPassFailure, setRecPassFailure] = useState(false);
    const [successState, setSuccessState] = useState(null);
    const successTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (successTimerRef.current) {
                window.clearTimeout(successTimerRef.current);
                successTimerRef.current = null;
            }
        };
    }, []);

    const initialValues = {
        Password: "",
        ConfirmPassword : ""
    };

    const {register, handleSubmit, watch, formState: {errors, isSubmitting}} = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema)
    });
    const passwordValue = watch("Password") ?? "";
    const confirmPasswordValue = watch("ConfirmPassword") ?? "";
    const passwordRuleStates = getPasswordRuleStates(passwordValue, confirmPasswordValue);
    const interactionLocked = isSubmitting || !!successState;

    const onSubmit = async (values) => {
        setRecPassFailure(false);
        const dataToSend = {...values,PasswordToken};
        const [data, status] = await postRequest("setpassword", dataToSend);
        if (status !== 200) {
            setSuccessState(null);
            setRecPassFailure(true);
        } else {
            const nextAction = () => {
                if (data?.offerPasskeyEnrollment && data?.accessToken) {
                    applyAuthSession(data);
                    navigate("/Account/Settings?onboarding=1&returnTo=%2F", { replace: true });
                    return;
                }

                navigate("/Auth/Login",{state: {passwordset: true}});
            };

            setSuccessState({
                title: "Password saved",
                copy: data?.offerPasskeyEnrollment
                    ? "Taking you to passkey setup now."
                    : "Taking you back to sign in."
            });

            if (successTimerRef.current) {
                window.clearTimeout(successTimerRef.current);
            }

            successTimerRef.current = window.setTimeout(() => {
                successTimerRef.current = null;
                nextAction();
            }, PASSWORD_SUCCESS_REDIRECT_DELAY_MS);
        }
    };


    return (
        <Container fluid className={styles.authViewport}>
            <div className={styles.authShell}>
                <div className={styles.formColumn}>
                    <Row className={styles.authRow}>
                        <Col xs={12} className={styles.authCol}>
                            <div className={styles.legalWrap}>
                                <div className={styles.legalDesktop}>
                                    <LegalWarning />
                                </div>
                            </div>
                            <Card className={styles.authCard}>
                                <Card.Img className={styles.banner} variant="top" src="/KTMAHEADER.png" alt="Money Follows The Person Banner"/>
                                <Card.Body className={styles.cardBody}>
                                    <h3 className={styles.title}>Set Password</h3>
                                    <p className={styles.subtitle}>Create a password to finish activating your account or complete a password reset.</p>

                                    {
                                        !PasswordToken ? (
                                            <InsetAlert type="Error" heading="Error!" body="A problem occured while loading the page. Please contact your system administrator." dismissible={false} style={{width: '100%'}}/>
                                        ) : (
                                            <>
                                                {showRecPassFailure && error ? (
                                                    <InsetAlert error={error} style={{width: '100%'}} />
                                                ) : null}

                                                <Form onSubmit={handleSubmit(onSubmit)}>
                                                    <div className={styles.passwordRulesPanel}>
                                                        <p className={styles.passwordRulesTitle}>Before you submit, make sure your password:</p>
                                                        <ul className={styles.passwordRulesList}>
                                                            {passwordRuleStates.map((rule) => (
                                                                <li
                                                                    key={rule.id}
                                                                    className={`${styles.passwordRuleItem} ${rule.met ? styles.passwordRuleItemMet : ""}`}
                                                                >
                                                                    <span
                                                                        className={`${styles.passwordRuleIndicator} ${rule.met ? styles.passwordRuleIndicatorMet : ""}`}
                                                                        aria-hidden="true"
                                                                    />
                                                                    <span>{rule.label}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                    <FloatingLabel controlId="set-password" label="New Password" className='mb-2'>
                                                        <Form.Control
                                                            type="password"
                                                            placeholder="Password"
                                                            autoComplete="new-password"
                                                            isInvalid={!!errors.Password}
                                                            disabled={interactionLocked}
                                                            {...register("Password")}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.Password?.message}
                                                        </Form.Control.Feedback>
                                                    </FloatingLabel>
                                                    <FloatingLabel controlId="set-confirm-password" label="Confirm Password" className='mb-2'>
                                                        <Form.Control
                                                            type="password"
                                                            placeholder="Confirm Password"
                                                            autoComplete="new-password"
                                                            isInvalid={!!errors.ConfirmPassword}
                                                            disabled={interactionLocked}
                                                            {...register("ConfirmPassword")}
                                                        />
                                                        <Form.Control.Feedback type="invalid">
                                                            {errors.ConfirmPassword?.message}
                                                        </Form.Control.Feedback>
                                                    </FloatingLabel>
                                                    {successState ? (
                                                        <div className={styles.otpSuccessWrap} role="status" aria-live="polite">
                                                            <span className={styles.otpSuccessIcon} aria-hidden="true" />
                                                            <div>
                                                                <p className={styles.otpSuccessTitle}>{successState.title}</p>
                                                                <p className={styles.otpSuccessCopy}>{successState.copy}</p>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                    <div className={styles.actionRow}>
                                                        <div />
                                                        <Button className={styles.primaryButton} variant="primary" type="submit" disabled={interactionLocked}>
                                                            {isSubmitting ? <DotLoader ButtonStyle={true} /> : <>Submit</>}
                                                        </Button>
                                                    </div>
                                                </Form>
                                            </>
                                        )
                                    }
                                </Card.Body>
                            </Card>
                            <details className={styles.legalMobile}>
                                <summary className={styles.legalMobileSummary}>Legal Warning</summary>
                                <div className={styles.legalMobileBody}>
                                    <LegalWarning />
                                </div>
                            </details>
                        </Col>
                    </Row>
                </div>
            </div>
        </Container>
        
    )
}

export default SetPassword;
